import 'leaflet/dist/leaflet.css';

import L from 'leaflet';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Marker, Popup } from 'react-leaflet';

import { usePhotoFilter } from '@/hooks/usePhotoFilter';
import { DEFAULT_POSITION, usePosition } from '@/hooks/usePosition';
import { api } from '@/lib/api';
import { PhotoGeo } from '@/types';

import { CustomZoomSlider } from './CustomZoomSlider';
import { EditSelectCombo } from './EditSelectCombo';
import { LocateUserControl } from './LocateUserControl';
import Map from './Map';
import { MinimapControl } from './MinimapControl';
import { ResetViewControl } from './ResetViewControl';

// Set default Leaflet marker icon globally (consistent with Map.tsx)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const LocationEditor = ({ geo, onGeoChange }: {
  geo: PhotoGeo | null;
  onGeoChange: (geo: PhotoGeo) => void;
}) => {
  const {
    states,
    provinces,
    regions,
    names,
  } = usePhotoFilter();

  const { coords, getPosition } = usePosition({ autoFetch: false, defaultCoords: DEFAULT_POSITION as [number, number] });

  const [coordsToFind, setCoordsToFind] = useState<string>(`${geo?.lat || ''}, ${geo?.lng || ''}`);

  // Update coordsToFind when geo changes externally, but avoid setting "undefined, undefined"
  useEffect(() => {
    if (geo?.lat !== undefined && geo?.lng !== undefined) {
      setCoordsToFind(`${geo.lat}, ${geo.lng}`);
    }
  }, [geo?.lat, geo?.lng]);

  // REMOVE geo from dependencies completely - this was causing the callback to recreate every time geo changed
  const updateFromApi = useCallback((lat: number, lng: number, currentGeo?: PhotoGeo | null) => {
    // Validate coordinates before making API call
    if (!isFinite(lat) || !isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      console.error('Invalid coordinates for lookup:', lat, lng);
      return;
    }

    api.get(`/lookup?lat=${lat}&lng=${lng}`)
      .then(response => {
        if (response.data.error) {
          console.error('Error looking up location:', response.data.error);
          return;
        }

        console.log('response.data', response.data);

        const newGeo: PhotoGeo = {
          // NOTE:
          //   we deliberately ignore the lat/lng from the API
          //   response because it seems to vary a bit from the actual
          //   query, and we want the map to best reflect the user's
          //   intent
          lat: lat,
          lng: lng,
          name: response.data.name || '',
          region: response.data.region || '',
          province: response.data.province || '',
          state: response.data.state || '',
          country_code: response.data.country_code || '',
          // Preserve any other existing properties from current geo
          ...(currentGeo && Object.keys(currentGeo).reduce<Partial<PhotoGeo>>((acc, key) => {
            if (!['lat', 'lng', 'name', 'region', 'province', 'state', 'country_code'].includes(key)) {
              const value = currentGeo[key as keyof PhotoGeo];
              if (value !== undefined) {
                acc[key as keyof PhotoGeo] = value as any;
              }
            }
            return acc;
          }, {}))
        };
        onGeoChange(newGeo);
      })
      .catch(error => {
        console.error('Error fetching location data:', error);
      });
  }, [onGeoChange]);

  useEffect(() => {
    if (coords) {
      // Pass current geo to updateFromApi
      updateFromApi(coords[0], coords[1], geo);
      setCoordsToFind(`${coords[0]}, ${coords[1]}`);
    }
  }, [coords, updateFromApi, geo?.lat, geo?.lng]); // Add minimal geo dependencies

  useEffect(() => {
    if (
      geo?.lat !== undefined &&
      geo?.lng !== undefined &&
      isFinite(geo.lat) &&
      isFinite(geo.lng) &&
      geo.name === undefined &&
      geo.region === undefined &&
      geo.province === undefined &&
      geo.state === undefined &&
      geo.country_code === undefined
    ) {
      updateFromApi(geo.lat, geo.lng, geo);
      setCoordsToFind(`${geo.lat}, ${geo.lng}`);
    }
  }, [geo?.lat, geo?.lng, geo?.name, geo?.region, geo?.province, geo?.state, geo?.country_code]); // Add minimal geo dependencies

  // Pass current geo to callbacks instead of using functional updates
  const handleFieldChange = useCallback((field: keyof PhotoGeo, value: string | number) => {
    if (!geo) return;
    console.log('handleFieldChange', field, value);
    const newGeo: PhotoGeo = { ...geo, [field]: value };
    onGeoChange(newGeo);
  }, [geo, onGeoChange]);

  const handleMarkerDragEnd = useCallback((e: any) => {
    if (!geo) return;
    const lat = e.target.getLatLng().lat;
    const lng = e.target.getLatLng().lng;
    const newGeo: PhotoGeo = { ...geo, lat, lng };
    onGeoChange(newGeo);

    updateFromApi(lat, lng, geo);
    setCoordsToFind(`${lat}, ${lng}`);
  }, [geo, onGeoChange, updateFromApi]);

  // Memoize the marker position to prevent recreation - only when geo exists
  const markerPosition: [number, number] | null = useMemo(() =>
    geo ? [geo.lat, geo.lng] : null, [geo?.lat, geo?.lng]
  );

  // Memoize the reset view center to prevent recreation - only when geo exists
  const resetViewCenter: [number, number] | null = useMemo(() =>
    geo ? [geo.lat, geo.lng] : null, [geo?.lat, geo?.lng]
  );

  const handleAddLocation = useCallback(async () => {
    await getPosition();
  }, [getPosition]);

  // Memoize the extraControls to prevent recreation on every render
  const extraControls = useMemo(() => (
    <>
      <CustomZoomSlider sliderPosition="leftside" />
      {resetViewCenter && (
        <ResetViewControl position="bottomright" center={resetViewCenter} className="mr-12" />
      )}
      <LocateUserControl position="bottomright" />
      <MinimapControl position="topright" />
    </>
  ), [resetViewCenter]);

  // Memoize the eventHandlers object to prevent recreation
  const markerEventHandlers = useMemo(() => ({
    dragend: handleMarkerDragEnd
  }), [handleMarkerDragEnd]);

  // Memoize the button onClick handler to prevent recreation
  const handleFindClick = useCallback(() => {
    const [lat, lng] = coordsToFind
      .split(/[,\s|()]/)
      .filter((s) => s.length > 0)
      .slice(0, 2)
      .map(Number);

    if (lat && lng) {
      handleFieldChange('lat', lat);
      handleFieldChange('lng', lng);
      updateFromApi(lat, lng, geo);
      setCoordsToFind(`${lat}, ${lng}`);
    }
  }, [coordsToFind, handleFieldChange, updateFromApi, geo]);

  // NOW we can do conditional rendering after all hooks are called
  if (!geo) {
    return (
      <div className="w-full text-center">
        <a className="italic cursor-pointer" onClick={handleAddLocation}>Add location...</a>
      </div>
    );
  }

  const name = geo.name || '';
  const state = geo.state || '';
  const province = geo.province || '';
  const region = geo.region || '';

  return (
    <div className="space-y-4">
      <div>
        {/* TODO: I'm not sure this should have the `select` aspect */}
        <EditSelectCombo
          value={name}
          onChange={(value) => handleFieldChange('name', value)}
          options={names}
          placeholder="Name..."
        />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <EditSelectCombo
            value={state}
            onChange={(value) => handleFieldChange('state', value)}
            options={states}
            placeholder="State..."
          />
        </div>
        <div>
          <EditSelectCombo
            value={province}
            onChange={(value) => handleFieldChange('province', value)}
            options={provinces}
            placeholder="Province..."
          />
        </div>
        <div>
          <EditSelectCombo
            value={region}
            onChange={(value) => handleFieldChange('region', value)}
            options={regions}
            placeholder="Region..."
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="flex flex-row col-span-2">
          <div className="flex-1 px-4 py-2 mr-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            <input type="text"
              className="w-full text-right"
              value={coordsToFind}
              onChange={(e) => setCoordsToFind(e.target.value)}
              placeholder="Latitude, Longitude..."
            />
          </div>
          <div className="flex justify-start px-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            <button
              type="submit"
              onClick={handleFindClick}
              className="px-5 py-1 text-white transition-colors bg-blue-400 rounded-md cursor-pointer hover:bg-blue-600"
            >
              Find ⬇️
            </button>
          </div>
        </div>
      </div>
      <div className='h-64'>
        <Map
          geo={geo}
          extraControls={extraControls}>
          {markerPosition && (
            <Marker
              position={markerPosition}
              draggable={true}
              eventHandlers={markerEventHandlers}
            >
              <Popup>
                {geo.name}, {geo.region}, {geo.province}, {geo.state}
              </Popup>
            </Marker>
          )}
        </Map>
      </div>
    </div>
  );
};

export { LocationEditor };
